import { forwardRef } from 'react';
import type { ElementType, ReactNode } from 'react';
import clsx from 'clsx';

import { usePaginationContext } from './Pagination.context';
import type { PolymorphicComponentProps, PolymorphicRef } from '@lib/types';

type PaginationControlOwnProps<E extends ElementType = 'button'> = {
  component?: E;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
};

export type PaginationControlProps<E extends ElementType = 'button'> = PolymorphicComponentProps<
  E,
  PaginationControlOwnProps<E>
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PaginationControlInner = forwardRef<any, PaginationControlProps>(
  <C extends ElementType = 'button'>(props: PaginationControlProps<C>, ref: React.Ref<Element>) => {
    const { component, active, className, disabled, children, ...rest } = props;

    const ctx = usePaginationContext();

    const Component = component || 'button';

    return (
      <Component
        {...rest}
        ref={ref as React.Ref<never>}
        disabled={disabled}
        data-active={active || undefined}
        data-disabled={disabled || undefined}
        className={clsx(
          'pagination-control cursor-pointer',
          [
            active
              ? 'active this:primary bg-this disabled:bg-this-light dark:bg-this-light dark:disabled:bg-this-darker text-white disabled:cursor-not-allowed disabled:opacity-60'
              : [
                  disabled
                    ? 'disabled:cursor-not-allowed disabled:opacity-60'
                    : 'dark:hover:bg-surface-1 dark:focus-visible:bg-surface-1 dark:active:bg-surface-1/90 hover:bg-gray-300 focus-visible:bg-gray-300 active:bg-gray-300/80',
                ],
          ],
          ctx.classNames?.control,
          className
        )}
      >
        {children}
      </Component>
    );
  }
);

type PaginationControlComponent = (<E extends ElementType = 'button'>(
  props: PaginationControlProps<E> & { ref?: PolymorphicRef<E> }
) => ReactNode) & { displayName?: string };

const PaginationControl = PaginationControlInner as PaginationControlComponent;

PaginationControl.displayName = 'PaginationControl';

export { PaginationControl };
